// h2h.js — helpers for the head-to-head pages (/head-to-head and
// /head-to-head/arsenal-vs-chelsea). A pair's URL puts the two club slugs in
// alphabetical order, so each pair has exactly one page; the other order
// redirects to it.

import CLUBS from "./clubs.json";
import { clubInfo, scorelineKey } from "./data";
import { seasonStartYear } from "../heatmap/utils";

// Pairs that met at least this often (two seasons) are pre-rendered, listed
// in the sitemap and indexed. Every pair that has met has a working page;
// one-season pairings (two meetings) are too thin to offer search engines.
export const H2H_INDEX_MIN = 4;

const slugToTeam = new Map(Object.entries(CLUBS).filter(([k]) => k !== "_comment").map(([team, c]) => [c.slug, team]));
export const teamForSlug = (slug) => slugToTeam.get(slug) || null;

// Canonical order: alphabetical by slug.
export const h2hOrder = (teamA, teamB) => (clubInfo(teamA).slug < clubInfo(teamB).slug ? [teamA, teamB] : [teamB, teamA]);
export const h2hPath = (teamA, teamB) => {
  const [a, b] = h2hOrder(teamA, teamB);
  return `/head-to-head/${clubInfo(a).slug}-vs-${clubInfo(b).slug}`;
};
export const h2hQuery = (teamA, teamB) => `h2h=${encodeURIComponent(h2hOrder(teamA, teamB).join(","))}`;

// "arsenal-vs-chelsea" -> ["Arsenal", "Chelsea"] (stored names), or null.
export function parsePair(pair) {
  const parts = (pair || "").split("-vs-");
  if (parts.length !== 2) return null;
  const [a, b] = parts.map(teamForSlug);
  return a && b && a !== b ? [a, b] : null;
}

// Famous fixtures for the hub, by club slug. Names are the ones fans use.
export const DERBIES = [
  ["arsenal", "tottenham-hotspur", "North London derby"],
  ["everton", "liverpool", "Merseyside derby"],
  ["manchester-city", "manchester-united", "Manchester derby"],
  ["liverpool", "manchester-united", "North West derby"],
  ["newcastle-united", "sunderland", "Tyne–Wear derby"],
  ["brighton", "crystal-palace", "M23 derby"],
  ["aston-villa", "birmingham-city", "Second City derby"],
  ["west-bromwich-albion", "wolverhampton-wanderers", "Black Country derby"],
  ["portsmouth", "southampton", "South Coast derby"],
  ["sheffield-united", "sheffield-wednesday", "Steel City derby"],
  ["derby-county", "nottingham-forest", "East Midlands derby"],
  ["arsenal", "chelsea", "London derby"],
  ["chelsea", "tottenham-hotspur", "London derby"],
  ["arsenal", "manchester-united", "Title rivals"],
  ["chelsea", "manchester-united", "Title rivals"],
  ["liverpool", "manchester-city", "Title rivals"],
];

// Everything the page shows, from club A's side (a = the first club in the
// URL). items: the API's ?h2h rows.
export function h2hStats(teamA, teamB, items) {
  const meetings = items
    .map((m) => {
      const aHome = m.homeTeam === teamA;
      const hs = Number(m.homeTeamScore);
      const as = Number(m.awayTeamScore);
      const ag = aHome ? hs : as;
      const bg = aHome ? as : hs;
      return { ...m, hs, as, aHome, ag, bg, result: ag > bg ? "a" : ag < bg ? "b" : "d", season: seasonStartYear(m.utcDate) };
    })
    .sort((x, y) => x.utcDate.localeCompare(y.utcDate));
  const count = (list, r) => list.filter((m) => m.result === r).length;
  const venue = (aHome) => {
    const list = meetings.filter((m) => m.aHome === aHome);
    return { played: list.length, a: count(list, "a"), d: count(list, "d"), b: count(list, "b"), ag: list.reduce((s, m) => s + m.ag, 0), bg: list.reduce((s, m) => s + m.bg, 0) };
  };
  // Longest runs, and the current one, for each side.
  const runs = (side) => {
    let win = 0, bestWin = 0, unbeaten = 0, bestUnbeaten = 0;
    for (const m of meetings) {
      win = m.result === side ? win + 1 : 0;
      unbeaten = m.result !== (side === "a" ? "b" : "a") ? unbeaten + 1 : 0;
      bestWin = Math.max(bestWin, win);
      bestUnbeaten = Math.max(bestUnbeaten, unbeaten);
    }
    return { bestWin, bestUnbeaten, win, unbeaten };
  };
  const margin = (m) => Math.abs(m.ag - m.bg);
  const byMargin = (side) =>
    meetings.filter((m) => m.result === side).sort((x, y) => margin(y) - margin(x) || y.ag + y.bg - (x.ag + x.bg) || y.utcDate.localeCompare(x.utcDate));
  const cells = new Map();
  for (const m of meetings) cells.set(`${m.ag}-${m.bg}`, (cells.get(`${m.ag}-${m.bg}`) || 0) + 1);
  const common = [...cells]
    .map(([key, n]) => ({ key, n, ag: Number(key.split("-")[0]), bg: Number(key.split("-")[1]) }))
    .sort((x, y) => y.n - x.n || x.key.localeCompare(y.key));
  return {
    meetings,
    played: meetings.length,
    a: count(meetings, "a"),
    d: count(meetings, "d"),
    b: count(meetings, "b"),
    ag: meetings.reduce((s, m) => s + m.ag, 0),
    bg: meetings.reduce((s, m) => s + m.bg, 0),
    atA: venue(true),
    atB: venue(false),
    runsA: runs("a"),
    runsB: runs("b"),
    biggestA: byMargin("a").slice(0, 3),
    biggestB: byMargin("b").slice(0, 3),
    highest: [...meetings].sort((x, y) => y.ag + y.bg - (x.ag + x.bg) || y.utcDate.localeCompare(x.utcDate)).slice(0, 3),
    common: common.slice(0, 5),
    cells,
    maxGoals: Math.max(5, ...meetings.map((m) => Math.max(m.ag, m.bg))),
    first: meetings[0],
    last: meetings[meetings.length - 1],
    scoreline: (m) => scorelineKey(m.hs, m.as),
  };
}
